<template>
  <div class="login-container">
    <div class="login-card">
      <!-- Language Switcher -->
      <div class="language-switcher-wrapper">
        <LoginLanguageSwitcher storage-key="seva_locale" />
      </div>

      <!-- Logo -->
      <div class="login-logo">
        <img src="/images/logo.png" alt="OnePlaceHQ" class="logo-image" />
        <span class="module-text">Seva</span>
      </div>

      <!-- Context Selection (shown after login when multiple contexts available) -->
      <template v-if="requiresSelection">
        <div class="login-header">
          <h1 class="login-title">{{ $t('login.selectContext') || 'Select Account' }}</h1>
          <p class="login-subtitle">{{ $t('login.selectContextSubtitle') || 'Choose which account to access' }}</p>
        </div>

        <div class="context-list">
          <div
            v-for="ctx in contexts"
            :key="ctx.profile_id"
            class="context-option"
            @click="handleSelectContext(ctx)"
          >
            <div class="context-business">{{ ctx.business_name }}</div>
            <div class="context-details">
              <span class="context-role">{{ ctx.role }}</span>
              <span class="context-modules">{{ ctx.modules?.join(', ') }}</span>
            </div>
          </div>
        </div>

        <IntercomButton variant="ghost" size="sm" block @click="cancelSelection">
          {{ $t('common.back') || 'Back to Login' }}
        </IntercomButton>
      </template>

      <!-- Login Form -->
      <template v-else>
        <div class="login-header">
          <h1 class="login-title">{{ $t('login.title') }}</h1>
          <p class="login-subtitle">{{ $t('login.subtitle') }}</p>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-field">
            <IntercomPhoneOrEmailInput
              v-model="formData.phone"
              :label="$t('login.identifier')"
              placeholder="Enter 10-digit phone or email"
              required
              :disabled="isLoading"
              @keyup.enter="handleLogin"
            />
          </div>

          <div class="form-field">
            <IntercomInput
              v-model="formData.password"
              type="password"
              :label="$t('login.password')"
              placeholder="Enter password"
              required
              :disabled="isLoading"
              @keyup.enter="handleLogin"
            />
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <!-- Submit Button -->
          <IntercomButton
            type="submit"
            variant="primary"
            size="lg"
            block
            :loading="isLoading"
            :loading-text="$t('login.signingIn')"
          >
            {{ $t('login.signIn') }}
          </IntercomButton>
        </form>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { IntercomButton, IntercomInput, IntercomPhoneOrEmailInput, LoginLanguageSwitcher } from '@shared/components/intercom'

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()
const { login, selectContext, cancelSelection, isLoading, requiresSelection, contexts } = useAuth()

// Form data
const formData = reactive({
  phone: '',
  password: '',
})

// Error message
const errorMessage = ref('')

/**
 * Handle login
 */
const handleLogin = async () => {
  try {
    errorMessage.value = ''

    // Basic validation
    if (!formData.phone || !formData.password) {
      errorMessage.value = t('login.errorRequired')
      return
    }

    // Validate phone or email format
    const isPhone = /^\d{10}$/.test(formData.phone)
    const isEmail = /^[\w\-\.]+@([\w\-]+\.)+[\w\-]{2,4}$/.test(formData.phone)
    if (!isPhone && !isEmail) {
      errorMessage.value = 'Enter a valid 10-digit phone or email address'
      return
    }

    // Login
    const response = await login(formData.phone, formData.password)

    // If context selection is required, the UI will update automatically
    if (response.requires_selection) {
      return
    }

    // Set locale from user's business settings
    if (response.user?.business?.default_locale) {
      locale.value = response.user.business.default_locale
    }

    // Redirect to Dashboard or original page
    const redirect = route.query.redirect || '/'
    router.push(redirect)
  } catch (error) {
    // Translate license errors using i18n keys
    if (error.i18nKey) {
      errorMessage.value = t(error.i18nKey, error.i18nParams || {})
    } else if (error.message?.includes('Invalid credentials')) {
      errorMessage.value = t('login.invalidCredentials')
    } else if (error.message?.includes('no access configured')) {
      errorMessage.value = t('login.noModuleAccess')
    } else {
      errorMessage.value = error.message || t('login.invalidCredentials')
    }
  }
}

/**
 * Handle context selection
 */
const handleSelectContext = async (ctx) => {
  try {
    errorMessage.value = ''

    const response = await selectContext(ctx.profile_id)

    // Set locale from user's business settings
    if (response.user?.business?.default_locale) {
      locale.value = response.user.business.default_locale
    }

    // Redirect to Dashboard or original page
    const redirect = route.query.redirect || '/'
    router.push(redirect)
  } catch (error) {
    // Translate license errors using i18n keys
    if (error.i18nKey) {
      errorMessage.value = t(error.i18nKey, error.i18nParams || {})
    } else {
      errorMessage.value = error.message || 'Selection failed'
    }
  }
}
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: var(--color-gray-50);
  padding: var(--space-4);
}

.login-card {
  width: 100%;
  max-width: 420px;
  padding: var(--space-12);
  background: var(--color-white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-8);
  gap: var(--space-1);
}

.logo-image {
  height: 48px;
  width: auto;
}

.module-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 2px;
}

.login-header {
  text-align: center;
  margin-bottom: var(--space-8);
}

.login-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0 0 var(--space-2) 0;
}

.login-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.error-message {
  padding: var(--space-3) var(--space-4);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

/* Context Selection Styles */
.context-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}

.context-option {
  padding: var(--space-4);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
}

.context-option:hover {
  border-color: var(--color-blue);
  background: var(--color-gray-50);
}

.context-business {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin-bottom: var(--space-1);
}

.context-details {
  display: flex;
  gap: var(--space-3);
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.context-role {
  text-transform: capitalize;
  font-weight: var(--font-weight-medium);
}

.context-modules {
  color: var(--color-gray-400);
}

@media (max-width: 480px) {
  .login-card {
    padding: var(--space-8);
  }

  .login-title {
    font-size: 26px;
  }
}

/* Language Switcher */
.language-switcher-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: var(--space-4);
}
</style>
